import { useEffect, useRef, useState } from "react";
import { RefundProtectionManager } from "@shared/modules/basket/services/refundProtection/RefundProtectionManager";
import { DataProvider } from "@shared/constants/common";
import { APP_ENV, PROTECHT_API_KEY } from "@shared/env";
import { useUpdateEffect } from "@shared/hooks/useUpdateEffect";
import { CURRENCY_MAP } from "@shared/constants/enums";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import {
  isRefundProtectionMetricsDryRun,
  logDryRunMetric,
} from "@shared/modules/basket/services/refundProtection/helpers";
import {
  addCustomMetric,
  markAllComponentsReady,
  markPageStart,
  trackError as trackDatadogError,
} from "@shared/utils/datadog/datadog-timings";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import { useBasketService } from "../../context/BasketServiceContext";

export interface RefundProtectionProps {
  basketType: DataProvider;
  onFormComplete: () => void;
  onError: (error?: unknown) => void;
  onLoaded: () => void;
  isEligible: boolean;
  protectionApiKey?: string; // optional in case there is an error loading it
  shouldShow: boolean;
}

export default function RefundProtection({
  basketType,
  onFormComplete,
  onError,
  onLoaded,
  isEligible = false,
  shouldShow = false,
  protectionApiKey = PROTECHT_API_KEY,
}: RefundProtectionProps) {
  const { modifyBasket, itemGroups, currency } = useBasketService();
  const { t } = useTranslation("common");
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [refundProtectionManager] = useState(
    () =>
      new RefundProtectionManager(
        basketType,
        modifyBasket,
        onFormComplete,
        onError,
        isEligible,
        shouldShow
      )
  );

  // Track timeout ID for callback fallback
  const callbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track if E2E timing has started (to avoid duplicate marks)
  const hasStartedE2ETimingRef = useRef(false);

  // Use refs for callbacks to avoid reconfiguring when callbacks change
  // The actual callbacks are updated separately via updateOnError/updateOnLoaded/updateOnFormComplete
  const onErrorRef = useRef(onError);
  const onLoadedRef = useRef(onLoaded);
  const onFormCompleteRef = useRef(onFormComplete);

  // Keep refs in sync with latest callbacks
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);

  useEffect(() => {
    onFormCompleteRef.current = onFormComplete;
  }, [onFormComplete]);

  useEffect(() => {
    refundProtectionManager.updateModifyBasket(modifyBasket);
  }, [modifyBasket]);

  useEffect(() => {
    const wrappedOnFormComplete = () => {
      // Clear timeout when user interacts with the widget
      if (callbackTimeoutRef.current) {
        clearTimeout(callbackTimeoutRef.current);
        callbackTimeoutRef.current = null;
      }
      onFormCompleteRef.current();
    };
    refundProtectionManager.updateOnFormComplete(wrappedOnFormComplete);
  }, []);

  useEffect(() => {
    const wrappedOnError = (error?: unknown) => {
      // Clear timeout when widget reports an error
      if (callbackTimeoutRef.current) {
        clearTimeout(callbackTimeoutRef.current);
        callbackTimeoutRef.current = null;
      }
      onErrorRef.current(error);
    };
    refundProtectionManager.updateOnError(wrappedOnError);
  }, []);

  // Reusable function to apply the click propagation fix
  const applyClickFix = (): (() => void) | null => {
    let attempts = 0;
    const maxAttempts = 10;
    let cleanupFn: (() => void) | null = null;

    const tryApply = () => {
      const portalDiv = document.querySelector(".chakra-portal");
      const ph = document.getElementById("tg-placeholder");

      if (!portalDiv || !ph) {
        if (attempts < maxAttempts) {
          attempts += 1;
          setTimeout(tryApply, 100); // Retry every 100ms
        }
        return;
      }

      // eslint-disable-next-line func-names
      const handler = function (e: Event) {
        const isWidgetClick = ph.contains(e.target as Node) || ph === e.target;

        if (isWidgetClick) {
          const originalStop = e.stopPropagation;
          // eslint-disable-next-line func-names
          e.stopPropagation = function () {
            // Prevent React from stopping propagation for widget clicks
          };

          setTimeout(() => {
            e.stopPropagation = originalStop;
          }, 0);
        }
      };

      portalDiv.addEventListener("click", handler, true);

      cleanupFn = () => {
        portalDiv.removeEventListener("click", handler, true);
      };
    };

    tryApply();
    return cleanupFn;
  };

  // Pass the fix function to the manager so it can reapply on widget updates
  useEffect(() => {
    refundProtectionManager.updateOnWidgetChange(applyClickFix);
  }, []);

  useEffect(() => {
    const handleLoaded = () => {
      // Clear timeout when widget loads successfully
      if (callbackTimeoutRef.current) {
        clearTimeout(callbackTimeoutRef.current);
        callbackTimeoutRef.current = null;
      }

      // Mark E2E timing completion
      if (hasStartedE2ETimingRef.current) {
        if (isRefundProtectionMetricsDryRun()) {
          logDryRunMetric("timing", "refund_protection_all_components_ready", {
            basket_type: basketType,
            is_eligible: isEligible,
          });
        } else {
          markAllComponentsReady("refund_protection", {
            basket_type: basketType,
            is_eligible: isEligible,
          });
        }
      }

      setWidgetLoaded(true);
      onLoadedRef.current();
    };
    refundProtectionManager.updateOnLoaded(handleLoaded);
  }, [basketType, isEligible]);

  useUpdateEffect(() => {
    if (shouldShow) {
      refundProtectionManager.enableInternalTracking();
    } else {
      refundProtectionManager.disableInternalTracking();
      setWidgetLoaded(false); // Reset widget loaded state
    }
  }, [shouldShow]);

  useEffect(() => {
    if (!shouldShow) {
      // Reset E2E timing tracking when widget is hidden
      hasStartedE2ETimingRef.current = false;
      return undefined;
    }

    // Start E2E timing when widget is shown
    if (!hasStartedE2ETimingRef.current) {
      if (isRefundProtectionMetricsDryRun()) {
        logDryRunMetric("timing", "refund_protection_page_start", {
          basket_type: basketType,
          is_eligible: isEligible,
        });
      } else {
        markPageStart("refund_protection", {
          basket_type: basketType,
          is_eligible: isEligible,
        });
      }
      hasStartedE2ETimingRef.current = true;
    }

    // Clear any existing timeout
    if (callbackTimeoutRef.current) {
      clearTimeout(callbackTimeoutRef.current);
      callbackTimeoutRef.current = null;
    }

    try {
      refundProtectionManager.configure({
        apiKey: protectionApiKey,
        sandbox: APP_ENV !== "production",
        items: RefundProtectionManager.buildItemsFromItemGroups(itemGroups),
        currency: currency || CURRENCY_MAP.CAD.id,
      });

      // Start timeout after successful configuration
      // The 'loaded' callback should fire if the widget is loaded, which will clear the timeout
      // If no callbacks fire within 3 seconds, treat it as an error to unblock checkout
      // 3 seconds chosen to:
      // - Allow widget time for API calls and rendering
      // - Align with 3-second fallback timer used elsewhere in payment flow (PaymentSectionRegularBaskets)
      // - Provide better UX than longer timeouts during active checkout
      callbackTimeoutRef.current = setTimeout(() => {
        callbackTimeoutRef.current = null;

        // Track timeout in Datadog
        if (isRefundProtectionMetricsDryRun()) {
          logDryRunMetric("metric", "refund_protection_timeout", {
            basket_type: basketType,
            timeout_ms: 3000,
          });
          logDryRunMetric("error", "refund_protection_timeout_error", {
            component: "RefundProtection",
            error_source: "timeout",
            timeout_ms: 3000,
            error_message: "Refund protection widget silently failed to load",
          });
        } else {
          addCustomMetric("refund_protection_timeout", 3000, {
            basket_type: basketType,
            timeout_ms: 3000,
          });
          trackDatadogError(
            new Error("Refund protection widget silently failed to load"),
            {
              component: "RefundProtection",
              error_source: "timeout",
              timeout_ms: 3000,
            }
          );
        }

        recordAppError(
          new Error("Refund protection widget silently failed to load")
        );
        onErrorRef.current();
      }, 3000);
    } catch (error) {
      // Track configuration error in Datadog
      if (isRefundProtectionMetricsDryRun()) {
        logDryRunMetric("error", "refund_protection_configure_error", {
          component: "RefundProtection",
          error_source: "configure_sync_error",
          error_message:
            error instanceof Error
              ? error.message
              : "RefundProtection configuration failed",
        });
      } else {
        trackDatadogError(
          error instanceof Error
            ? error
            : new Error("RefundProtection configuration failed"),
          {
            component: "RefundProtection",
            error_source: "configure_sync_error",
          }
        );
      }
      // Configuration failed synchronously - don't block checkout
      onErrorRef.current(error);
      return undefined;
    }

    // Apply the initial click propagation fix
    const cleanupFn = applyClickFix();

    return () => {
      // Clear timeout on cleanup
      if (callbackTimeoutRef.current) {
        clearTimeout(callbackTimeoutRef.current);
        callbackTimeoutRef.current = null;
      }
      refundProtectionManager.destroy();
      if (cleanupFn) {
        cleanupFn();
      }
    };
  }, [shouldShow, basketType, isEligible]);

  // Determine if we should show the message
  // Show when widget is being shown and has successfully loaded
  const shouldShowMessage = shouldShow && widgetLoaded;

  return (
    <>
      {/* Show message only when widget is eligible and has successfully loaded */}
      <Box display={shouldShowMessage ? "block" : "none"} mb="md">
        <Text size="sm">
          {t(
            "Refund protection only applies to tickets - packages are not supported"
          )}
        </Text>
      </Box>
      <div id="tg-placeholder" />
    </>
  );
}
